import { useState } from 'react';

/**
 * One question a visitor can actually answer, marked on the spot, with the
 * sentence the answer came from lit up in the passage — what Target does for
 * a student, done for the centre owner reading about it.
 *
 * The passage and statements are written for this page, not taken from a
 * paper. Three statements, one of each answer, so all three can be tried.
 */
const SENTENCES = [
  'Paper maps were once the only way to find a route across a city.',
  'By the 1990s, most delivery drivers in London carried a printed atlas, and a driver who knew the streets by heart could earn noticeably more.',
  'Satellite navigation changed this within a decade: by 2010, fewer than one in five drivers still relied on paper.',
  'Some companies now ask new drivers to pass a map-reading test anyway, arguing that screens fail more often than people expect.',
];

type Answer = 'TRUE' | 'FALSE' | 'NOT GIVEN';

const STATEMENTS: { text: string; answer: Answer; evidence: number | null; why: string }[] = [
  {
    text: 'By 2010, most London delivery drivers no longer used a paper atlas.',
    answer: 'TRUE',
    evidence: 2,
    why: 'Fewer than one in five still relied on paper.',
  },
  {
    text: 'Drivers who knew the streets by heart earned less than other drivers.',
    answer: 'FALSE',
    evidence: 1,
    why: 'They could earn noticeably more.',
  },
  {
    text: 'Satellite navigation was first sold in London.',
    answer: 'NOT GIVEN',
    evidence: null,
    why: 'The passage never says where it was first sold.',
  },
];

const OPTIONS: Answer[] = ['TRUE', 'FALSE', 'NOT GIVEN'];

export function TryQuestion() {
  const [index, setIndex] = useState(0);
  const [chosen, setChosen] = useState<Answer | null>(null);
  const statement = STATEMENTS[index]!;
  const marked = chosen !== null;
  const right = chosen === statement.answer;

  function next() {
    setChosen(null);
    setIndex((i) => (i + 1) % STATEMENTS.length);
  }

  return (
    <div className="tryq">
      <div className="tryq__passage">
        <b>Getting around</b>
        <p>
          {SENTENCES.map((sentence, i) => (
            <span
              key={i}
              className={`tryq__sentence${marked && statement.evidence === i ? ' is-evidence' : ''}`}
            >
              {sentence}{' '}
            </span>
          ))}
        </p>
      </div>

      <div className="tryq__question">
        <p className="tryq__rubric">Does the statement agree with the information in the passage?</p>
        <p className="tryq__stem">
          <span className="tryq__n">{index + 1}</span>
          {statement.text}
        </p>
        <div className="tryq__options" role="radiogroup" aria-label="Your answer">
          {OPTIONS.map((option, i) => {
            const state = !marked
              ? ''
              : option === statement.answer
                ? ' is-right'
                : option === chosen
                  ? ' is-wrong'
                  : '';
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={chosen === option}
                className={`tryq__opt${chosen === option ? ' is-chosen' : ''}${state}`}
                disabled={marked}
                onClick={() => setChosen(option)}
              >
                <span className="tryq__letter">{'ABC'[i]}</span>
                <span className="tryq__radio" />
                <span>{option}</span>
              </button>
            );
          })}
        </div>

        <div className="tryq__verdict" aria-live="polite">
          {marked && (
            <div className={`tryq__mark${right ? ' tryq__mark--right' : ''}`} key={`${index}-${chosen}`}>
              <p>
                <b>{right ? 'Correct.' : `Not quite: it’s ${statement.answer}.`}</b> {statement.why}
                {statement.evidence !== null && ' The sentence is marked in the passage.'}
              </p>
              <button type="button" className="tryq__next" onClick={next}>
                Try another
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
